import { planzoneSettings } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrievePlanzoneSettingsRequest(
  state: State,
  planzone: Boundaries["retrievePlanzoneSettings"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          settings: planzoneSettings,
        },
      ];
}

function handleRetrievePlanzoneSettingsSuccess(
  state: State,
  { planzone, settings }: Boundaries["retrievePlanzoneSettings"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), settings },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          settings,
        },
      ];
}

function handleRetrievePlanzoneSettingsFailure(
  state: State,
  planzone: Boundaries["retrievePlanzoneSettings"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          settings: planzoneSettings,
        },
      ];
}

function handleEditPlanzoneSettingsRequest(
  state: State,
  { planzone, nextSettings }: Boundaries["editPlanzoneSettings"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], settings: nextSettings },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          settings: nextSettings,
        },
      ];
}

function handleEditPlanzoneSettingsSuccess(
  state: State,
  { planzone, settings }: Boundaries["editPlanzoneSettings"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], settings },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          settings,
        },
      ];
}

function handleEditPlanzoneSettingsFailure(
  state: State,
  { planzone, settings }: Boundaries["editPlanzoneSettings"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], settings },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          settings,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrievePlanzoneSettingsRequested]:
    handleRetrievePlanzoneSettingsRequest,
  [events.retrievePlanzoneSettingsSucceeded]:
    handleRetrievePlanzoneSettingsSuccess,
  [events.retrievePlanzoneSettingsFailed]:
    handleRetrievePlanzoneSettingsFailure,
  [events.editPlanzoneSettingsRequested]: handleEditPlanzoneSettingsRequest,
  [events.editPlanzoneSettingsSucceeded]: handleEditPlanzoneSettingsSuccess,
  [events.editPlanzoneSettingsFailed]: handleEditPlanzoneSettingsFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
